import * as CryptoJS from 'crypto-js';
import { Buffer } from 'buffer';

const MAJOR = Buffer.from([0x48, 0x89]);
const MINOR_SALT = 'HAUDI_AUTO_LOCK_UNLOCK_SHA2_SALT-FOR-MAJOR-MINOR';

interface SocialDoorSettings {
  isGuideBellEnabled: boolean;
  isSignageEnabled: boolean;
  isSpeakerEnabled: boolean;
  idInCluster: number;
}

const emptySocialDoorSettings: SocialDoorSettings = {
  isGuideBellEnabled: false,
  isSignageEnabled: false,
  isSpeakerEnabled: false,
  idInCluster: 0,
};

/**
 * Decode Haudi-base64 string to the original bytes array.
 * @param src Haudi-base64 encoded string.
 */
const decodeHaudiBase64 = (src: string): Buffer => {
  // in haudi-base64, '/' is encoded as '_' and '+' as '-'
  let b64str = src.replace('_', '/').replace('-', '+');

  // in haudi-base64, the suffix '='s are stripped
  const remainder = b64str.length % 4;
  if (remainder > 0) {
    b64str += '='.repeat(4 - remainder);
  }

  return Buffer.from(b64str, 'base64');
};

/**
 * Given a haudi-UUID string, manages the conversion to normal UUID and its
 * minor value of BLE beacon.
 */
export class HaudiUuid {
  public haudiUuid: string;

  private isAalEnabled: boolean;
  private isSciEnabled: boolean;
  private socialDoorSettings: SocialDoorSettings;

  private uuid: Buffer;
  private uuidHash: CryptoJS.lib.WordArray;

  constructor(
    uuid: string,
    isAalEnabled?: boolean,
    isSciEnabled?: boolean,
    socialDoorSettings?: SocialDoorSettings
  ) {
    this.haudiUuid = uuid;
    this.isAalEnabled = isAalEnabled ?? false;
    this.isSciEnabled = isSciEnabled ?? false;
    this.socialDoorSettings = socialDoorSettings ?? emptySocialDoorSettings;

    this.uuid = decodeHaudiBase64(this.haudiUuid);
    
    if (this.uuid.length === 16) {
      // the UUID is authentic; calculate SHA256 hash
      const cat = Buffer.concat([
        this.uuid.reverse(), // to little-endian
        Buffer.from(MINOR_SALT, 'utf8'),
      ]);
      this.uuidHash = CryptoJS.SHA256(CryptoJS.lib.WordArray.create(cat));
    } else {
      // set the UUID value and the hash to the iterations of 0
      this.uuid = Buffer.from(Array(16).fill(0));
      this.uuidHash = CryptoJS.lib.WordArray.create([0]);
    }
  }

  /**
   * Set the parameters related to its BLE beacon minor value.
   */
  public setParams(
    isAalEnabled: boolean,
    isSciEnabled: boolean,
    socialDoorSettings: SocialDoorSettings
  ) {
    this.isAalEnabled = isAalEnabled;
    this.isSciEnabled = isSciEnabled;
    this.socialDoorSettings = socialDoorSettings;
  }

  /**
   * Convert the original UUID byte array (with its length of 16) to the
   * well-known RFC-formatted UUID style.
   * @example '0ead1ccb-e30d-473a-b25b-ff9d931bb3f0'
   */
  public getRfcFormattedUuid() {
    const h = this.uuid.reverse().toString('hex');
    return (
      h.substring(0, 8) +
      '-' +
      h.substring(8, 12) +
      '-' +
      h.substring(12, 16) +
      '-' +
      h.substring(16, 20) +
      '-' +
      h.substring(20)
    );
  }

  /**
   * Return its BLE beacon major value, which is a constant value currently.
   */
  public getMajorValue() : Buffer {
    return MAJOR;
  }

  /**
   * Calculate its BLE beacon minor value. Return value always has its length
   * of 2.
   */
  public getMinorValue(): Buffer {
    // in the scope, minor should always keep its length to 2
    let minor = Buffer.from(Array(2).fill(0));

    if (this.isAalEnabled) {
      // Autolock feature is enabled; minor is calculated based on the hash
      // calculated by the UUID; set the minor to its first 2 bytes
      // (in little-endian)
      minor = Buffer.from([
        (this.uuidHash.words[0] >>> 16) & 0xff,
        (this.uuidHash.words[0] >>> 24) & 0xff,
      ]);

      // up Autolock feature enabled bit
      minor = Buffer.from([minor[0] | 0x40, minor[1]]);

      if (this.isSciEnabled) {
        // up Social feature enabled bit
        minor = Buffer.from([minor[0] | 0x80, minor[1]]);
      } else {
        // down Social feature enabled bit
        minor = Buffer.from([minor[0] & 0x7f, minor[1]]);
      }
    } else if (this.isSciEnabled) {
      // only Social feature is enabled; minor is calculated based on its
      // social feature settings
      let upper = minor[0];
      if (this.socialDoorSettings.isGuideBellEnabled) {
        upper = upper | 0x20;
      }
      if (this.socialDoorSettings.isSignageEnabled) {
        upper = upper | 0x10;
      }
      if (this.socialDoorSettings.isSpeakerEnabled) {
        upper = upper | 0x08;
      }
      upper = upper | (0x07 & this.socialDoorSettings.idInCluster);

      // ...and up Social feature enabled bit
      minor = Buffer.from([upper | 0x80, minor[1]]);
    }

    return minor;
  }
}
